// services/school-ui/src/pages/chat/api.ts
// Wraps nova-os PR #399's /v1/conversations CRUD. All four endpoints
// are JWT-authenticated by Nova OS — `user_id` is read from the
// bearer's `sub` claim. Vite's dev proxy doesn't auto-attach the SPA's
// auth header (unlike the promotion-svc proxy which injects X-User-Id
// from env), and the production gateway expects each request to carry
// its own bearer too, so we pass authHeader explicitly from every
// callsite (sidebar + chat page).

import type { Conversation, ConversationDetail } from "./types";
import { createClientUuid } from "@/clientId";

type AuthHeader = Record<string, string>;

export class ConvForbidden extends Error {
  constructor() {
    super("forbidden");
    this.name = "ConvForbidden";
  }
}

export class ConvNotFound extends Error {
  constructor() {
    super("not found");
    this.name = "ConvNotFound";
  }
}

export async function fetchConversations(
  authHeader: AuthHeader,
  agent: string,
  limit = 50,
): Promise<Conversation[]> {
  const url = `/v1/conversations?agent=${encodeURIComponent(agent)}&limit=${limit}`;
  const r = await fetch(url, { headers: authHeader });
  if (!r.ok) throw new Error(`fetch conversations: ${r.status}`);
  const body = (await r.json()) as { conversations: Conversation[] };
  return body.conversations ?? [];
}

export async function fetchConversation(
  authHeader: AuthHeader,
  id: string,
): Promise<ConversationDetail> {
  const r = await fetch(`/v1/conversations/${encodeURIComponent(id)}`, {
    headers: authHeader,
  });
  if (r.status === 403) throw new ConvForbidden();
  if (r.status === 404) throw new ConvNotFound();
  if (!r.ok) throw new Error(`fetch conversation: ${r.status}`);
  return (await r.json()) as ConversationDetail;
}

export async function renameConversation(
  authHeader: AuthHeader,
  id: string,
  title: string,
): Promise<Conversation> {
  const r = await fetch(`/v1/conversations/${encodeURIComponent(id)}`, {
    method: "PATCH",
    headers: { ...authHeader, "Content-Type": "application/json" },
    body: JSON.stringify({ title }),
  });
  if (r.status === 403) throw new ConvForbidden();
  if (r.status === 404) throw new ConvNotFound();
  if (!r.ok) {
    const body = (await r.json().catch(() => ({}))) as { error?: string };
    throw new Error(body.error ?? `rename failed: ${r.status}`);
  }
  return (await r.json()) as Conversation;
}

/** Ask school-backend to summarize a conversation's first turn into a short
 *  ChatGPT-style title. Degrades server-side to the first line of the user
 *  message when the gateway/key is unavailable, so it always resolves to a
 *  usable string. Talks to the school-backend AI router, not Nova OS. */
export async function generateConversationTitle(
  authHeader: AuthHeader,
  userMessage: string,
  assistantMessage: string,
  locale: string,
): Promise<string> {
  const r = await fetch("/api/school/ai/title", {
    method: "POST",
    headers: { ...authHeader, "Content-Type": "application/json" },
    body: JSON.stringify({
      user_message: userMessage,
      assistant_message: assistantMessage,
      locale,
    }),
  });
  if (!r.ok) throw new Error(`title ${r.status}`);
  return ((await r.json()) as { title: string }).title;
}

export async function deleteConversation(
  authHeader: AuthHeader,
  id: string,
): Promise<void> {
  const r = await fetch(`/v1/conversations/${encodeURIComponent(id)}`, {
    method: "DELETE",
    headers: authHeader,
  });
  if (r.status === 403) throw new ConvForbidden();
  if (r.status === 404) throw new ConvNotFound();
  if (!r.ok) throw new Error(`delete failed: ${r.status}`);
}

// Client-side UUID for a "new" conversation. Used as conversation_id
// in the AG-UI POST body when the user clicks [+ New]. Nova OS creates
// the row server-side on the first message.
export function generateClientConversationId(): string {
  return `chat_${createClientUuid()}`;
}
